import { Editor } from '@tiptap/core';
import { buildExtensions } from 'manage/tiptap/extensions';
import {
  findLostContent,
  markBareStampsSyntax,
  normalizeLegacyHtml,
  tidyTiptapHtml,
} from 'manage/tiptap/html';
import alertify from 'shared/alertify';

// 選択肢は CKEditor 4 の既定（font_names、fontSize_sizes、colorButton_colors）と同じにして、保存されている値と合わせる
const FONT_FAMILIES = [
  ['Arial', 'Arial, Helvetica, sans-serif'],
  ['Comic Sans MS', 'Comic Sans MS, cursive'],
  ['Courier New', 'Courier New, Courier, monospace'],
  ['Georgia', 'Georgia, serif'],
  ['Lucida Sans Unicode', 'Lucida Sans Unicode, Lucida Grande, sans-serif'],
  ['Tahoma', 'Tahoma, Geneva, sans-serif'],
  ['Times New Roman', 'Times New Roman, Times, serif'],
  ['Trebuchet MS', 'Trebuchet MS, Helvetica, sans-serif'],
  ['Verdana', 'Verdana, Geneva, sans-serif'],
];
const FONT_SIZES = [
  8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72,
].map((size) => `${size}px`);
const COLORS = [
  '#1abc9c',
  '#2ecc71',
  '#3498db',
  '#9b59b6',
  '#4e5f70',
  '#f1c40f',
  '#16a085',
  '#27ae60',
  '#2980b9',
  '#8e44ad',
  '#2c3e50',
  '#f39c12',
  '#e67e22',
  '#e74c3c',
  '#ecf0f1',
  '#95a5a6',
  '#dddddd',
  '#ffffff',
  '#d35400',
  '#c0392b',
  '#bdc3c7',
  '#7f8c8d',
  '#999999',
  '#000000',
];
const FORMATS = [
  ['paragraph', '標準'],
  ...[1, 2, 3, 4, 5, 6].map((level) => [`heading${level}`, `見出し ${level}`]),
];

const createElement = (tagName, attributes = {}, children = []) => {
  const element = document.createElement(tagName);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'className') {
      element.className = value;
    } else if (name.startsWith('on')) {
      element.addEventListener(name.slice(2).toLowerCase(), value);
    } else {
      element.setAttribute(name, value);
    }
  }
  element.append(...children);
  return element;
};

const createButton = ({ icon, label, title, onClick }) =>
  createElement(
    'button',
    {
      type: 'button',
      className: 'c-tiptap__button',
      title,
      'aria-label': title,
      onMousedown: (event) => event.preventDefault(),
      onClick,
    },
    [
      ...(icon ? [createElement('i', { className: icon })] : []),
      ...(icon && label ? [' '] : []),
      ...(label ? [label] : []),
    ],
  );

const createSelect = ({ title, placeholder, options, onChange }) =>
  createElement(
    'select',
    { className: 'c-tiptap__select', title, 'aria-label': title, onChange },
    [
      createElement('option', { value: '' }, [placeholder]),
      ...options.map(([value, label]) =>
        createElement('option', { value }, [label]),
      ),
    ],
  );

// 画像と添付ファイルの受け口は CKEditor と共用で、検証で弾いたときも 200 で uploaded: 0 を返す
const upload = async (url, file) => {
  const body = new FormData();
  body.append('upload', file);
  const response = await fetch(url, {
    method: 'POST',
    body,
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token':
        document.querySelector('meta[name="csrf-token"]')?.content ?? '',
    },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.uploaded !== 1) {
    throw new Error(json.error?.message || 'アップロードできませんでした');
  }
  return json;
};

// textarea はフォームの値として残し、HTML の編集モードではそれをそのまま編集してもらう
export const replaceWithTiptap = (
  textarea,
  { imageUploadUrl, fileUploadUrl, extraButtons = [] },
) => {
  const content = createElement('div', { className: 'c-tiptap__content' });
  const notice = createElement('div', { className: 'c-tiptap__notice' });
  notice.hidden = true;
  const toolbar = createElement('div', { className: 'c-tiptap__toolbar' });
  const wrapper = createElement('div', { className: 'c-tiptap' }, [
    notice,
    toolbar,
    content,
  ]);
  // textarea を動かすと Stimulus が付け外しとみなし、connect と disconnect を繰り返す
  textarea.before(wrapper);
  textarea.classList.add('c-tiptap__source');

  const editor = new Editor({
    element: content,
    extensions: buildExtensions(),
  });

  const serialize = () =>
    editor.isEmpty ? '' : tidyTiptapHtml(editor.getHTML());

  // 開いただけでは HTML を書き換えないように、見た目の編集に入ったときの HTML と同じ間は元の HTML を保存する
  let sourceOnLoad = '';
  let serializedOnLoad = '';
  let sourceMode = false;

  // 属性名に使えない文字があるなど、Tiptap が描けない HTML は例外になるので、HTML の編集で開く
  const load = (html) => {
    try {
      editor.commands.setContent(
        markBareStampsSyntax(normalizeLegacyHtml(html)),
        { emitUpdate: false },
      );
      sourceOnLoad = html;
      serializedOnLoad = serialize();
      return findLostContent(normalizeLegacyHtml(html), serializedOnLoad);
    } catch {
      editor.commands.clearContent();
      return '読み込めない HTML があります';
    }
  };

  // HTML の編集の間は textarea が正なので、隠れているエディタで何か起きても書き戻さない
  editor.on('update', () => {
    if (sourceMode) return;
    const html = serialize();
    textarea.value = html === serializedOnLoad ? sourceOnLoad : html;
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  });

  const controls = [];
  const sourceButton = createButton({
    icon: 'fas fa-code',
    label: 'ソース',
    title: 'HTML の編集',
    onClick: () => (sourceMode ? enterVisualMode() : enterSourceMode()),
  });

  const setSourceMode = (value) => {
    sourceMode = value;
    textarea.hidden = !value;
    content.hidden = value;
    sourceButton.classList.toggle('is-active', value);
    for (const control of controls) control.disabled = value;
  };

  const enterSourceMode = (lostContent = null) => {
    notice.hidden = !lostContent;
    notice.textContent = lostContent
      ? `見た目の編集では扱えない HTML が含まれているため、HTML の編集で開いています（${lostContent}）`
      : '';
    setSourceMode(true);
  };

  const enterVisualMode = () => {
    const lostContent = load(textarea.value);
    const proceed = () => {
      notice.hidden = true;
      setSourceMode(false);
      editor.commands.focus();
    };
    if (!lostContent) {
      proceed();
      return;
    }
    // alertify は文言を HTML として描くので、上のお知らせ（textContent）と違ってタグ名や本文の文字をエスケープしないと、タグとして解釈されて消えたりスクリプトとして動いたりする
    const escapedLostContent = createElement('div', {}, [
      lostContent,
    ]).innerHTML;
    alertify.confirm(
      `見た目の編集では扱えない HTML が含まれています。切り替えて編集すると、保存したときに失われます。切り替えますか？（${escapedLostContent}）`,
      proceed,
      () => {},
    );
  };

  const run = (command) => command(editor.chain().focus()).run();

  const addControl = (control) => {
    controls.push(control);
    return control;
  };

  const toggles = [
    ['fas fa-bold', '太字', 'bold', (c) => c.toggleBold()],
    ['fas fa-italic', '斜体', 'italic', (c) => c.toggleItalic()],
    ['fas fa-underline', '下線', 'underline', (c) => c.toggleUnderline()],
    ['fas fa-strikethrough', '取り消し線', 'strike', (c) => c.toggleStrike()],
    [
      'fas fa-list-ol',
      '番号付きリスト',
      'orderedList',
      (c) => c.toggleOrderedList(),
    ],
    ['fas fa-list-ul', '箇条書き', 'bulletList', (c) => c.toggleBulletList()],
  ].map(([icon, title, name, command]) => ({
    name,
    button: addControl(
      createButton({ icon, title, onClick: () => run(command) }),
    ),
  }));

  const alignments = [
    ['left', 'fas fa-align-left', '左揃え'],
    ['center', 'fas fa-align-center', '中央揃え'],
    ['right', 'fas fa-align-right', '右揃え'],
    ['justify', 'fas fa-align-justify', '両端揃え'],
  ].map(([alignment, icon, title]) => ({
    alignment,
    button: addControl(
      createButton({
        icon,
        title,
        onClick: () =>
          run((c) =>
            editor.isActive({ textAlign: alignment })
              ? c.unsetTextAlign()
              : c.setTextAlign(alignment),
          ),
      }),
    ),
  }));

  const linkButton = addControl(
    createButton({
      icon: 'fas fa-link',
      title: 'リンク',
      onClick: () => {
        const current = editor.getAttributes('link').href ?? 'https://';
        alertify.prompt(
          'リンクの URL',
          current,
          (_event, href) =>
            run((c) =>
              href
                ? c.extendMarkRange('link').setLink({ href })
                : c.extendMarkRange('link').unsetLink(),
            ),
          () => {},
        );
      },
    }),
  );
  const unlinkButton = addControl(
    createButton({
      icon: 'fas fa-link-slash',
      title: 'リンクを外す',
      onClick: () => run((c) => c.extendMarkRange('link').unsetLink()),
    }),
  );

  // system spec の attach_file から選べるように、ファイルの選択は DOM に置いた input で受ける
  const createUploadButton = ({ icon, title, url, accept, insert }) => {
    const input = createElement('input', {
      type: 'file',
      className: 'c-tiptap__fileInput',
      accept,
      onChange: async () => {
        const [file] = input.files;
        input.value = '';
        if (!file) return;
        try {
          const uploaded = await upload(url, file);
          if (!editor.isDestroyed) insert(uploaded, file);
        } catch (error) {
          alertify.error(error.message);
        }
      },
    });
    const button = addControl(
      createButton({ icon, title, onClick: () => input.click() }),
    );
    return createElement('span', {}, [button, input]);
  };

  const imageButton = createUploadButton({
    icon: 'fas fa-image',
    title: '画像',
    url: imageUploadUrl,
    accept: 'image/*',
    insert: ({ url: src, width, height }) =>
      run((c) =>
        c.insertContent({
          type: 'image',
          attrs: { src, alt: '', width, height },
        }),
      ),
  });
  // CKEditor のリンクのダイアログと同じく、文字を選んでいればその文字をリンクにする
  const attachmentButton = createUploadButton({
    icon: 'fas fa-paperclip',
    title: 'ファイルを添付',
    url: fileUploadUrl,
    accept: 'application/pdf,image/*',
    insert: ({ url: href }, file) =>
      run((c) =>
        editor.state.selection.empty
          ? c.insertContent({
              type: 'text',
              text: file.name,
              marks: [{ type: 'link', attrs: { href } }],
            })
          : c.setLink({ href }),
      ),
  });

  const formatSelect = addControl(
    createSelect({
      title: '書式',
      placeholder: '書式',
      options: FORMATS,
      onChange: (event) => {
        const { value } = event.target;
        if (value === 'paragraph') run((c) => c.setParagraph());
        if (value.startsWith('heading')) {
          run((c) => c.setHeading({ level: Number(value.slice(-1)) }));
        }
      },
    }),
  );
  const fontFamilySelect = addControl(
    createSelect({
      title: 'フォント',
      placeholder: 'フォント',
      options: FONT_FAMILIES.map(([label, value]) => [value, label]),
      onChange: (event) =>
        run((c) =>
          event.target.value
            ? c.setFontFamily(event.target.value)
            : c.unsetFontFamily(),
        ),
    }),
  );
  const fontSizeSelect = addControl(
    createSelect({
      title: 'サイズ',
      placeholder: 'サイズ',
      options: FONT_SIZES.map((size) => [size, size.replace('px', '')]),
      onChange: (event) =>
        run((c) =>
          event.target.value
            ? c.setFontSize(event.target.value)
            : c.unsetFontSize(),
        ),
    }),
  );

  const createColorPicker = ({ icon, title, set, unset }) => {
    const palette = createElement('div', { className: 'c-tiptap__palette' }, [
      createElement(
        'button',
        {
          type: 'button',
          className: 'c-tiptap__paletteReset',
          onMousedown: (event) => event.preventDefault(),
          onClick: () => {
            palette.hidden = true;
            run(unset);
          },
        },
        ['自動'],
      ),
      ...COLORS.map((color) =>
        createElement('button', {
          type: 'button',
          className: 'c-tiptap__swatch',
          title: color,
          'aria-label': color,
          style: `background-color: ${color}`,
          onMousedown: (event) => event.preventDefault(),
          onClick: () => {
            palette.hidden = true;
            run((c) => set(c, color));
          },
        }),
      ),
    ]);
    palette.hidden = true;
    const button = addControl(
      createButton({
        icon,
        title,
        onClick: () => {
          palette.hidden = !palette.hidden;
        },
      }),
    );
    return createElement('span', { className: 'c-tiptap__colorPicker' }, [
      button,
      palette,
    ]);
  };

  const clearButton = addControl(
    createButton({
      icon: 'fas fa-eraser',
      title: '書式を解除',
      // CKEditor の書式の解除と同じく文字の書式だけを外し、カーソルのときは続けて打つ文字の書式も外す
      onClick: () =>
        run((c) =>
          c.unsetAllMarks().command(({ tr }) => {
            tr.setStoredMarks([]);
            return true;
          }),
        ),
    }),
  );

  const group = (...children) =>
    createElement('span', { className: 'c-tiptap__group' }, children);
  const toggleButton = (name) => toggles.find((t) => t.name === name).button;

  toolbar.append(
    group(sourceButton),
    group(linkButton, unlinkButton),
    group(imageButton, attachmentButton),
    group(
      toggleButton('orderedList'),
      toggleButton('bulletList'),
      ...alignments.map(({ button }) => button),
    ),
    createElement('span', { className: 'c-tiptap__break' }),
    group(formatSelect, fontFamilySelect, fontSizeSelect),
    group(
      createColorPicker({
        icon: 'fas fa-font',
        title: '文字色',
        set: (c, color) => c.setColor(color),
        unset: (c) => c.unsetColor(),
      }),
      createColorPicker({
        icon: 'fas fa-fill-drip',
        title: '背景色',
        set: (c, color) => c.setBackgroundColor(color),
        unset: (c) => c.unsetBackgroundColor(),
      }),
    ),
    group(
      toggleButton('bold'),
      toggleButton('italic'),
      toggleButton('underline'),
      toggleButton('strike'),
      clearButton,
    ),
  );
  if (extraButtons.length > 0) {
    toolbar.append(
      group(...extraButtons.map((button) => addControl(createButton(button)))),
    );
  }

  const refreshToolbar = () => {
    for (const { name, button } of toggles) {
      button.classList.toggle('is-active', editor.isActive(name));
    }
    for (const { alignment, button } of alignments) {
      button.classList.toggle(
        'is-active',
        editor.isActive({ textAlign: alignment }),
      );
    }
    const level = [1, 2, 3, 4, 5, 6].find((l) =>
      editor.isActive('heading', { level: l }),
    );
    formatSelect.value = level
      ? `heading${level}`
      : editor.isActive('paragraph')
        ? 'paragraph'
        : '';
    const { fontFamily, fontSize } = editor.getAttributes('textStyle');
    fontFamilySelect.value = fontFamily ?? '';
    fontSizeSelect.value = fontSize ?? '';
  };
  editor.on('transaction', refreshToolbar);

  const lostContent = load(textarea.value);
  if (lostContent) {
    enterSourceMode(lostContent);
  } else {
    setSourceMode(false);
  }

  return {
    insertText(text) {
      if (sourceMode) {
        textarea.setRangeText(
          text,
          textarea.selectionStart,
          textarea.selectionEnd,
          'end',
        );
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
      run((c) => c.insertContent({ type: 'text', text }));
    },
    destroy() {
      editor.destroy();
      textarea.classList.remove('c-tiptap__source');
      textarea.hidden = false;
      wrapper.remove();
    },
  };
};
